import { Autocomplete } from '@mui/material';
import React from 'react';
import {
  BLACK,
  DRAWER_INACTIVE_SELECTION,
  FITOGRAM_BLUE_TINTED,
} from '~/Constants/Colors.constants';
import { FONT_SIZE_INBETWEEN, FONT_SIZE_MEDIUM_INBETWEEN } from '~/Constants/UI.constants';
import { useDateFns } from '~/Hooks';
import { IEvent } from '~/Models/DTO/Event.model';
import { makeStyles } from '~/UIKit/Style';

export interface IComboboxOptions extends IEvent {
  locationName?: string;
  trainerNames?: string[];
}

export interface IComboBoxProps {
  options: IComboboxOptions[];
  renderInput: (params: any) => JSX.Element;
  onChange: (event: React.SyntheticEvent, value: IComboboxOptions | null) => void;
  value: IComboboxOptions | null;
  label?: string;
  noOptionsText?: string;
  renderOptions?: (props: any, option: IComboboxOptions) => JSX.Element;
  extraClasses?: {
    label?: string;
    combobox?: string;
  };
  openOnFocus?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: 8,
    marginBottom: 24,
  },
  endAdornment: {
    '& svg': {
      fill: BLACK,
    },
  },
  input: {
    '&.MuiOutlinedInput-input.MuiAutocomplete-input': {
      padding: 0,
      fontSize: FONT_SIZE_INBETWEEN,
      lineHeight: '20px',
      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
  inputRoot: {
    '&.MuiOutlinedInput-root': {
      padding: '14px',
      '&:hover': {
        cursor: 'pointer',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: FITOGRAM_BLUE_TINTED,
        },
      },
    },
  },
  listbox: {
    padding: 0,
    border: `2px solid ${FITOGRAM_BLUE_TINTED}`,
    borderRadius: 6,
    maxHeight: '25vh',
    [theme.breakpoints.up('md')]: {
      maxHeight: '30vh',
    },
  },
  paper: {
    margin: '8px 0',
  },
  option: {
    '&.MuiAutocomplete-option': {
      padding: '12px 24px',
      borderBottom: `1px solid ${DRAWER_INACTIVE_SELECTION}`,
      justifyContent: 'space-between',
      columnGap: 12,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
  },
  noOption: {
    border: `2px solid ${FITOGRAM_BLUE_TINTED}`,
    color: 'currentColor',
    fontSize: FONT_SIZE_MEDIUM_INBETWEEN,
    lineHeight: '24px',
    fontWeight: 'bold',
  },
}));

export const ComboBox = ({
  options,
  label,
  renderInput,
  noOptionsText,
  openOnFocus,
  extraClasses,
  fullWidth = true,
  onChange,
  renderOptions,
  loading,
  value,
}: IComboBoxProps) => {
  const { classes } = useStyles();
  const { formatDate } = useDateFns();

  const inputLabel = (option: IComboboxOptions) =>
    `${option.name} - ${option.locationName} - ${formatDate(option.start, 'HH:mm')} to ${formatDate(
      option.end,
      'HH:mm',
    )}`;

  const customClasses = {
    root: classes.root,
    inputRoot: classes.inputRoot,
    endAdornment: classes.endAdornment,
    input: classes.input,
    listbox: classes.listbox,
    paper: classes.paper,
    option: classes.option,
    noOptions: classes.noOption,
  };

  return (
    <>
      {label && (
        <label className={extraClasses?.label} htmlFor="combobox">
          {label}
        </label>
      )}
      <Autocomplete<IComboboxOptions>
        onChange={onChange}
        value={value}
        options={options}
        noOptionsText={noOptionsText}
        openOnFocus={openOnFocus}
        loading={loading}
        clearOnBlur
        classes={customClasses}
        getOptionLabel={inputLabel}
        fullWidth={fullWidth}
        renderInput={renderInput}
        renderOption={renderOptions}
      />
    </>
  );
};
